import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO } from "@/constants/testIds";

const line = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.13, ease: [0.76, 0, 0.24, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={HERO.section}
      className="relative min-h-[100svh] flex flex-col justify-between overflow-hidden pt-28 pb-8 px-5 lg:px-10"
    >
        {/* Parallax product still-life on right — removed per request */}

        {/* Top overline row */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="relative z-10 flex justify-between items-start text-[#0A2A1E]"
        >
          <div className="section-rule overline">SC / 001 · Est. Rajasthan</div>
          <div className="overline hidden sm:block">
            Udaipur · Bhiwadi
          </div>
        </motion.div>

      {/* Main kinetic headline */}
      <div className="relative z-10 flex-1 flex flex-col justify-end">
        <div className="max-w-[1400px]">
          <div className="font-display text-[#0A2A1E] leading-[0.92] tracking-[-0.02em]">
            <span className="reveal-mask block">
              <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block text-[13vw] md:text-[11vw] lg:text-[9.5vw]">
                Controlling
              </motion.span>
            </span>
            <span className="reveal-mask block">
              <motion.span variants={line} initial="hidden" animate="show" custom={1} className="block text-[13vw] md:text-[11vw] lg:text-[9.5vw]">
                <span className="font-display-italic">moisture,</span>
              </motion.span>
            </span>
            <span className="reveal-mask block">
              <motion.span variants={line} initial="hidden" animate="show" custom={2} className="block text-[13vw] md:text-[11vw] lg:text-[9.5vw]">
                enhancing
              </motion.span>
            </span>
            <span className="reveal-mask block">
              <motion.span variants={line} initial="hidden" animate="show" custom={3} className="block text-[13vw] md:text-[11vw] lg:text-[9.5vw]">
                <span className="font-display-italic">quality.</span><span className="cursor-blink" />
              </motion.span>
            </span>
          </div>
        </div>

        {/* Sub row */}
        <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="md:col-span-6 lg:col-span-5 text-lg md:text-xl leading-relaxed text-[#1E3B30] max-w-xl"
          >
            SconeCORE Enterprises manufactures precision additives — quick
            lime, desiccants, calcium fillers and colour masterbatches — for the
            plastics industry of tomorrow.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55, duration: 0.9 }}
            className="md:col-span-6 lg:col-span-7 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <a href="#products" data-testid={HERO.ctaExplore} className="btn-primary">
              Explore the Range <ArrowUpRight size={14} />
            </a>
            <a href="#contact" data-testid={HERO.ctaInquire} className="btn-ghost text-[#0A2A1E]">
              Speak to Arnav
            </a>
          </motion.div>
        </div>

        {/* Bottom meta row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="mt-10 lg:mt-16 border-t border-[#0A2A1E]/15 pt-4 flex flex-wrap justify-between gap-4 text-[#0A2A1E]"
        >
          <div className="overline">Plastic Processing Additives</div>
          <div className="overline">
            (SC) — Serving All-India Since Day One
          </div>
          <div className="overline flex items-center gap-2">
            Scroll <ArrowDown size={12} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
